// Localise the alert message + type chip.
//
// Backend ships `message_key` + `message_args` (since migration 0013).
// Legacy rows have message_key === "" — fall back to the zh-TW `message`
// the evaluator hard-codes so existing data still renders. Custom admin
// rules use message_key="rule.threshold" but their `type` is the dynamic
// "rule:<metric>_<comparator>_<threshold>"; that one we keep as-is for
// the type chip with a translated prefix.

import type { TFunction } from "@faceheart/i18n";

export interface AlertI18nFields {
  message: string;
  message_key?: string;
  message_args?: Record<string, unknown>;
  type: string;
}

export function formatAlertMessage(t: TFunction, a: AlertI18nFields): string {
  if (a.message_key) {
    // Resolve metric/comparator into human-readable form before interpolation
    // so templates can say "Heart rate deviates..." instead of "hr deviates...".
    const args: Record<string, unknown> = { ...(a.message_args ?? {}) };
    if (typeof args.metric === "string") {
      const key = `trends.metrics.${args.metric}`;
      const resolved = t(key);
      if (resolved !== key) args.metric = resolved;
    }
    return t(`alerts.messages.${a.message_key}`, args);
  }
  return a.message;
}

export function formatAlertType(t: TFunction, type: string): string {
  // Custom admin rules are dynamic — render a generic prefix + the raw key.
  if (type.startsWith("rule:")) {
    return `${t("alerts.typeFallback.rule")} · ${type.slice("rule:".length)}`;
  }
  // i18next treats ":" as the namespace separator, so backend types like
  // "hardcoded:afib_detected" get mis-parsed. Normalise to "." for lookup.
  const normalised = type.replace(":", ".");
  const key = `alerts.types.${normalised}`;
  const out = t(key);
  return out === key ? type : out;
}
